import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImportQueue } from '../contexts/ImportQueueContext';
import {
    Upload, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

/* ── Utilidad: exportar reporte CSV ─────────────────────────────── */
function downloadReport(job) {
    const rows = [
        ['Expediente', 'Estado', 'Denominación', 'ID Marca', 'Error'],
        ...(job.results || []).map(r => [
            r.expediente,
            r.status === 'success' ? 'Importada' : r.status === 'duplicate' ? 'Duplicada' : 'Error',
            r.denominacion || '',
            r.trademark_id || '',
            r.error || ''
        ])
    ];
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-importacion-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

/* ── Componente principal ────────────────────────────────────────── */
export const BulkImportModal = ({ isOpen, onClose, clientId = null }) => {
    const { token } = useAuth();
    const { addExpedientes } = useImportQueue();

    // Estados del formulario
    const [rawText, setRawText] = useState('');
    const [parsedCount, setParsedCount] = useState(0);

    /* Parsear textarea → lista de expedientes */
    const parseExpedientes = useCallback((text) => {
        return [...new Set(
            text.split(/[\n,;]+/)
                .map(s => s.trim().toUpperCase())
                .filter(s => s.length > 0 && /^\d+$/.test(s))
        )];
    }, []);

    useEffect(() => {
        setParsedCount(parseExpedientes(rawText).length);
    }, [rawText, parseExpedientes]);

    const handleClose = () => {
        setRawText('');
        onClose();
    };

    const handleStart = async () => {
        const expedientes = parseExpedientes(rawText);
        if (expedientes.length === 0) {
            toast.error('No se encontraron expedientes válidos (solo números)');
            return;
        }

        addExpedientes(expedientes, clientId);
        toast.success(`${expedientes.length} expedientes agregados a la cola de importación.`);
        handleClose();
    };

    /* ── Render ── */
    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-gray-900">
                        <Upload className="h-5 w-5 text-indigo-600" />
                        Importación Masiva desde IMPI
                    </DialogTitle>
                    <DialogDescription>
                        Pega los números de expediente (uno por línea, o separados por comas).
                        El sistema consultará el IMPI por cada uno en paralelo.
                    </DialogDescription>
                </DialogHeader>

                {/* ── FASE 1: Formulario inicial ── */}
                <div className="space-y-4 py-2">
                    <textarea
                        className="w-full h-48 border border-gray-300 rounded-lg p-3 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder={"1234567\n2345678\n3456789\n\nO separados por comas: 1234567, 2345678, 3456789"}
                        value={rawText}
                        onChange={e => setRawText(e.target.value)}
                    />
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            {parsedCount > 0
                                ? <><span className="font-semibold text-indigo-700">{parsedCount}</span> expedientes válidos detectados</>
                                : 'Ingresa al menos un número de expediente'}
                        </p>
                        <p className="text-xs text-gray-400">Máximo 1,000 por importación</p>
                    </div>
                </div>

                {/* ── Footer ── */}
                <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
                    <Button variant="outline" onClick={handleClose}>Cancelar</Button>
                    <Button
                        disabled={parsedCount === 0}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={handleStart}
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        Agregar a la cola ({parsedCount})
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
